import { useRouter } from "next/router";
import { ReactElement, useEffect, useState, useCallback } from "react";

import { Box, Button, Container, Flex, Heading, Text } from "@chakra-ui/react";

import SidebarCollapse from "components/global/sidebar-collapse";
import {
  SidebarFilter,
  SidebarFilterCategory,
  SidebarFilterPrice,
  SidebarFilterQuantity,
  SidebarFilterStatus,
} from "components/global/sidebar-filter";
import { ItemCollection } from "components/items/item-collection";
import {
  DataDisplayUtilityBox,
  FilterUtility,
  GridSizeUtility,
  SearchInputUtility,
  SortUtility,
} from "components/items/item-utility";
import MainIndex from "components/layout/main-index";
import MainContainer from "components/main-container";

import { formatDistance } from "date-fns";
import { IoChevronDown } from "react-icons/io5";

import { GraphQLQuery } from "@aws-amplify/api";
import { API, graphqlOperation } from "aws-amplify";
import {
  Collection,
  CollectionStatus,
  Item as ItemProps,
  SearchCollectionsQuery,
  SearchableSortDirection,
  SearchableUserProfileSortableFields,
} from "src/API";
import { searchCollections, searchItems } from "src/graphql/queries";
import { useParameterStore } from "utils/context";
import { useDebounce } from "utils/helpers";
import {
  FilterValueProps,
  SortDataHandler,
  SortSelectionData,
} from "utils/interface";
import { GetServerSideProps } from "next";

interface NftItemsPropsObject {
  loaded: boolean;
  items: ItemProps[];
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { sale, search } = context.query;

  return {
    props: {
      sale: sale ?? null,
      search: search ?? null,
    },
  };
};

const Item = (props: {
  sale: string | string[];
  search: string | string[];
}) => {
  const { search, sale } = props;
  const { connectionUrl } = useParameterStore();

  const [items, setItems] = useState<NftItemsPropsObject>({
    loaded: false,
    items: [],
  });
  const [itemsNextToken, setItemNextToken] = useState<string>("");
  const [itemsData, setItemsData] = useState<{
    dateNow: Date;
    lastUpdated: Date;
    total: number;
  }>({
    dateNow: new Date(),
    lastUpdated: new Date(),
    total: 0,
  });
  const [trigger, setTrigger] = useState<boolean>(false);

  const [searchInput, setSearchInput] = useState<string>(
    search ? String(search) : ""
  );
  const debounceInput = useDebounce(searchInput, 500);
  const [selectedItemSort, setSelectedItemSort] = useState<string>();

  const optionsSortSelection: SortSelectionData[] = [
    {
      label: "Recently created",
      field: SearchableUserProfileSortableFields.createdAt,
      direction: SearchableSortDirection.desc,
    },
    {
      label: "Oldest created",
      field: SearchableUserProfileSortableFields.createdAt,
      direction: SearchableSortDirection.asc,
    },
    {
      label: "Recently updated",
      field: SearchableUserProfileSortableFields.updatedAt,
      direction: SearchableSortDirection.desc,
    },
    {
      label: "Oldest updated",
      field: SearchableUserProfileSortableFields.updatedAt,
      direction: SearchableSortDirection.asc,
    },
  ];

  const [itemsSort, setItemsSort] = useState<SortDataHandler>({
    dataSort: optionsSortSelection,
    value: selectedItemSort,
    handler: (value: string) => setSelectedItemSort(value),
  });
  const [itemFilter, setItemFilter] = useState<FilterValueProps>({
    dataFilter: ["On Sale"],
  });

  const getListItems = useCallback(
    async (
      searchName?: string,
      filterValue?: string | null,
      sortValue?: string,
      nextToken?: string
    ) => {
      setItems((i) => ({ ...i, loaded: false }));
      try {
        const getSearchableCollections = await API.graphql<
          GraphQLQuery<SearchCollectionsQuery>
        >({
          query: searchCollections,
          variables: {
            filter: {
              and: [
                { status: { ne: CollectionStatus.COMING_SOON } },
                { status: { ne: CollectionStatus.DRAFT } },
              ],
            },
          },
        });

        const searchableCollections = getSearchableCollections.data
          ?.searchCollections?.items as Collection[];

        if (searchableCollections.length > 0) {
          const items: any = await API.graphql(
            graphqlOperation(searchItems, {
              limit: 30,
              filter: {
                or: [
                  ...searchableCollections.map(({ id }) => ({
                    collectionId: {
                      eq: id,
                    },
                  })),
                ],
                and: [
                  ...(filterValue === "On Sale"
                    ? [
                        { recipientAddress: { exists: true } },
                        { recipientAddress: { ne: "" } },
                        { recipientAddress: { ne: "-" } },
                      ]
                    : []),
                  ...(searchName !== ""
                    ? [{ name: { matchPhrasePrefix: `*${searchName}` } }]
                    : []),
                ],
              },
              sort: sortValue
                ? [
                    {
                      field: sortValue.split(" ")[0],
                      direction: sortValue.split(" ")[1],
                    },
                  ]
                : {
                    field: "createdAt",
                    direction: "desc",
                  },
              nextToken: nextToken == "" ? null : nextToken,
            })
          );

          const data: any = items.data.searchItems;

          // show items with available collection status
          setItems((i) => ({
            loaded: true,
            items: [...i.items, ...data.items],
          }));
          setItemsData((d) => ({
            dateNow: new Date(),
            lastUpdated: new Date(),
            total: data.total,
          }));
          setItemNextToken(data.nextToken);
        } else {
          setItems({ loaded: true, items: [] });
          setItemNextToken("");
        }
      } catch (error) {
        console.log(error);
      }
    },
    []
  );

  useEffect(() => {
    if (trigger) {
      setTrigger((t) => !t);
      if (itemsNextToken !== null) {
        getListItems(
          debounceInput,
          itemFilter.value,
          selectedItemSort,
          itemsNextToken
        );
      }
    }
  }, [
    trigger,
    itemsNextToken,
    debounceInput,
    selectedItemSort,
    itemFilter.value,
    getListItems,
  ]);

  useEffect(() => {
    if (
      debounceInput ||
      debounceInput == "" ||
      selectedItemSort ||
      selectedItemSort !== ""
    ) {
      setItems({ loaded: false, items: [] });
      setItemNextToken("");
      setTrigger(true);
    }
  }, [debounceInput, selectedItemSort, itemFilter.value]);

  useEffect(() => {
    if (search) {
      setSearchInput(String(search));
    }
  }, [search]);

  useEffect(() => {
    // Sync date every 1 minute
    const timer = setInterval(() => {
      setItemsData((d) => ({ ...d, dateNow: new Date() }));
    }, 60 * 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (sale && sale === "active") {
      setItemFilter((prev) => ({ ...prev, value: "On Sale" }));
    }
  }, [sale]);

  return (
    <MainContainer title="Explore Items" maxW={"100%"} p={0}>
      <Box display="Flex" justifyContent="center" alignContent="center" mt={8}>
        <Heading as="h1" textAlign={"center"}>
          Explore Items
        </Heading>
      </Box>
      <Container maxW={"full"} px={0}>
        <SidebarCollapse
          topBar={
            <>
              <DataDisplayUtilityBox>
                <FilterUtility
                  placeholder={"All"}
                  filterValue={itemFilter}
                  filterHandler={setItemFilter}
                />
                <SearchInputUtility
                  width={{ base: "100%" }}
                  flex={1}
                  flexBasis={{ base: "100%", md: "auto" }}
                  value={searchInput}
                  handler={(value: string) => setSearchInput(value)}
                />
                <SortUtility
                  placeholder={""}
                  options={itemsSort.dataSort}
                  value={itemsSort.value}
                  handler={itemsSort.handler}
                />
                <GridSizeUtility />
              </DataDisplayUtilityBox>
              <Flex
                mt={4}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Text>
                  Updated{" "}
                  {formatDistance(itemsData.lastUpdated, itemsData.dateNow, {
                    addSuffix: true,
                  })}
                </Text>
                <Text>
                  {Intl.NumberFormat("en-IN", {
                    maximumSignificantDigits: 3,
                  }).format(itemsData.total)}{" "}
                  {itemsData.total > 1 ? "items" : "item"}
                </Text>
              </Flex>
            </>
          }
          sideBar={<Sidebar />}
          mainBar={
            <>
              <ItemCollection
                data={items.items}
                loaded={items.loaded}
                connectionUrl={connectionUrl}
              />
              <Box textAlign={"center"} mt={12}>
                {itemsNextToken && (
                  <Button
                    variant={"gradient"}
                    rightIcon={<IoChevronDown />}
                    onClick={() => {
                      setTrigger(true);
                    }}
                  >
                    Load More
                  </Button>
                )}
              </Box>
            </>
          }
        />
      </Container>
    </MainContainer>
  );
};

Item.getLayout = (page: ReactElement) => {
  return (
    <>
      <MainIndex>{page}</MainIndex>
    </>
  );
};

export default Item;

const Sidebar = () => {
  return (
    <SidebarFilter>
      <SidebarFilterStatus />
      <SidebarFilterPrice />
      <SidebarFilterQuantity />
      <SidebarFilterCategory />
    </SidebarFilter>
  );
};
